import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import z from "zod";
import { AlertAction, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	FormCard,
	FormCardAlert,
	FormCardContent,
	FormCardHeader,
	FormCardTitle,
} from "@/components/ui/form-card";
import { useCopy } from "@/hooks/use-copy";
import { orpc } from "@/orpc/client";

export const Route = createFileRoute("/_app/channels/$channelId/share")({
	component: Page,
	params: z.object({
		channelId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.channels.show.queryOptions({
				input: {
					channelId: params.channelId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();
	const { handleCopy } = useCopy();

	const { data: channel } = useSuspenseQuery(
		orpc.channels.show.queryOptions({
			input: {
				channelId: params.channelId,
			},
		}),
	);

	const embedCode = `<iframe
		title="Podcast player for channel ${channel.UID}"
		style="width: 100%; height: 80px;"
		src="${import.meta.env.VITE_PODCAST_EMBED_URL}/${channel.UID}"
></iframe>`;

	return (
		<div className="grid grid-cols-12 gap-6 px-4 lg:px-6">
			<FormCard className="col-span-12">
				<FormCardHeader>
					<FormCardTitle>Channel Page</FormCardTitle>
				</FormCardHeader>
				<FormCardAlert>
					<AlertTitle>Podcast Page</AlertTitle>
					<AlertDescription>
						Link to the public page of your podcast, where listeners can find more information about
						your show and listen to episodes.
					</AlertDescription>
					<AlertAction>
						<Button
							variant="outline"
							nativeButton={false}
							render={
								<a
									href={`${import.meta.env.VITE_PODCAST_EMBED_URL}/${channel.UrlTitle}`}
									target="_blank"
									rel="noopener noreferrer"
								/>
							}
						>
							Open Channel page
						</Button>
					</AlertAction>
				</FormCardAlert>
			</FormCard>
			<FormCard className="col-span-12">
				<FormCardHeader>
					<FormCardTitle>Channel Embed Player</FormCardTitle>
				</FormCardHeader>
				<FormCardAlert>
					<AlertTitle>Podcast Embed Played</AlertTitle>
					<AlertDescription>
						Embed the podcast player on your own website or blog to allow visitors to listen to your
						episodes without leaving your site.
					</AlertDescription>
					<AlertAction>
						<Button variant="outline" onClick={() => handleCopy(embedCode)}>
							Copy Code
						</Button>
					</AlertAction>
				</FormCardAlert>
				<FormCardContent className="mt-1">
					<pre className="bg-muted rounded-lg p-4">
						<code>{embedCode}</code>
					</pre>
				</FormCardContent>
				<FormCardContent
					className="mt-1"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: This is a trusted embed code generated by our own system, and we need to render it as HTML to display the podcast player.
					dangerouslySetInnerHTML={{ __html: embedCode }}
				/>
			</FormCard>
		</div>
	);
}
